import { clsx } from 'clsx'
import { Check } from 'lucide-react'
import { Button } from './Button'

export function TierCard({
  name,
  description,
  price,
  priceSuffix = '/mo',
  priceNote,
  vendorCap,
  features,
  ctaLabel,
  ctaHref,
  highlighted = false,
  muted = false,
  badge,
  disabled = false,
  className,
}: {
  name: string
  description: string
  price: string
  priceSuffix?: string
  priceNote?: string
  vendorCap?: string
  features: string[]
  ctaLabel: string
  ctaHref: string
  highlighted?: boolean
  muted?: boolean
  badge?: string
  disabled?: boolean
  className?: string
}) {
  return (
    <div
      className={clsx(
        className,
        'relative flex flex-col rounded-3xl p-8 ring-1',
        highlighted
          ? 'bg-gray-950 text-white ring-gray-950'
          : muted
            ? 'bg-gray-50 text-gray-950 ring-black/5'
            : 'bg-white text-gray-950 ring-black/10',
      )}
    >
      {badge && (
        <span className="absolute -top-3 left-8 inline-flex items-center rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold text-white">
          {badge}
        </span>
      )}
      <h3 className="font-display text-lg font-semibold">{name}</h3>
      <p
        className={clsx(
          'mt-2 text-sm',
          highlighted ? 'text-gray-300' : 'text-gray-600',
        )}
      >
        {description}
      </p>
      <div className="mt-6 flex items-baseline gap-1">
        <span className="tnum font-display text-4xl font-medium tracking-tight">
          {price}
        </span>
        {priceSuffix && (
          <span
            className={clsx(
              'text-sm',
              highlighted ? 'text-gray-400' : 'text-gray-500',
            )}
          >
            {priceSuffix}
          </span>
        )}
      </div>
      {priceNote && (
        <p
          className={clsx(
            'mt-1 text-xs',
            highlighted ? 'text-gray-400' : 'text-gray-500',
          )}
        >
          {priceNote}
        </p>
      )}
      {vendorCap && (
        <p className="mt-4 text-sm font-medium">{vendorCap}</p>
      )}
      <div className="mt-6">
        {disabled ? (
          <Button variant="outline" disabled className="w-full">
            {ctaLabel}
          </Button>
        ) : highlighted ? (
          <Button
            href={ctaHref}
            variant="outline"
            className="w-full border-white/20 bg-white text-gray-950 ring-white/20 hover:bg-gray-100"
          >
            {ctaLabel}
          </Button>
        ) : (
          <Button href={ctaHref} className="w-full">
            {ctaLabel}
          </Button>
        )}
      </div>
      <ul
        className={clsx(
          'mt-8 space-y-3 text-sm',
          highlighted ? 'text-gray-300' : 'text-gray-600',
        )}
      >
        {features.map((feature) => (
          <li key={feature} className="flex gap-3">
            <Check
              className={clsx(
                'size-5 shrink-0',
                highlighted ? 'text-white' : 'text-indigo-600',
              )}
              aria-hidden="true"
            />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
